import Image from 'next/image';
import Link from 'next/link';

import { Button } from '@oresundsbron/bridge-ui';

import {
  Corporate5050Props,
  CorporateNavigationBlockProps,
  CorporateBanner as CorporateBannerType,
} from '@oresundsbron/api/src/types/contentful';

import CorporateNavigationBlock from './CorporateNavigationBlock';
import Corporate5050 from './Corporate5050';
import Corporate5050Wrapper from './Corporate5050Wrapper';
import { useLinksV2 } from '~/hooks';

interface CorporateBannerProps {
  content: CorporateBannerType;
}

function CorporateBanner({
  content: {
    title,
    media,
    preTitle,
    preamble,
    link,
    linkText,
    solidBackground,
    content,
  },
}: CorporateBannerProps) {
  const { getLink } = useLinksV2();

  const backgroundWithWaveForm = () => {
    return (
      media && (
        <>
          <Image
            src={media?.url!}
            alt={media?.description || ''}
            fill
            className="z-0 object-cover"
            sizes="(max-width: 1760px) 100vw,
               1760px"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 1920 132"
            className="absolute -bottom-px left-0 right-0"
          >
            <path
              className="scale-[1.01]"
              fill="#fff"
              d="M922.028 58.395c-33.657 8.283-62.495 15.358-111.211 24.504-207.992 36.239-397.717 29.681-520.14 17.947C116.014 84.106 1.282 50.456 0 50.112V131h1920V92.562c-1.28-.344-116.65-44.694-291.96-71.614C1524.85 5.072 1421.33-1.83 1320.38.413c-98.71 2.07-196.14 13.115-289.07 32.787l-11.54 2.416c-42.942 9.319-72.11 16.394-97.742 22.779Z"
              opacity=".2"
            />
            <path
              className="scale-[1.01]"
              fill="#fff"
              d="M922.028 76.569c-33.657 8.308-62.495 15.404-111.211 24.577-207.992 36.347-397.717 29.77-520.14 18C116.014 102.357 1.282 68.607 0 68.261v63.154h1920v-20.576c-1.28-.346-116.65-44.828-291.96-71.828-103.19-15.924-206.71-22.847-307.66-20.597-98.71 2.077-196.14 13.154-289.07 32.885l-11.54 2.423c-42.942 9.347-72.11 16.443-97.742 22.847Z"
            />
          </svg>
        </>
      )
    );
  };

  return (
    <>
      <div className="relative mb-6 pb-10 pt-10 sm:mb-0 sm:pb-56 sm:pt-28">
        <div className="relative z-10 mx-auto max-w-4xl text-white">
          {solidBackground ? (
            <div className="mx-4 rounded-2xl bg-primary-950 p-6 sm:p-16">
              {preTitle && (
                <div className="mb-4 font-semibold uppercase tracking-wide text-primary-400">
                  {preTitle}
                </div>
              )}
              <h1 className="text-4xl font-semibold leading-tight sm:text-5xl">
                {title}
              </h1>
              {preamble && (
                <div className="mt-6 max-w-prose text-lg leading-relaxed">
                  {preamble}
                </div>
              )}
              {link?.slug && linkText && (
                <Button
                  className="mt-6 sm:mt-8"
                  as={Link}
                  href={getLink(link?.sys.id)?.path!}
                  color="white"
                  intent="outlined"
                >
                  {linkText}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center">
              {preTitle && (
                <div className="mb-4 font-semibold uppercase tracking-wide">
                  {preTitle}
                </div>
              )}
              <h1 className="mb-4 text-4xl font-semibold leading-tight sm:text-6xl">
                {title}
              </h1>
              {preamble && (
                <div className="max-w-prose text-center text-lg sm:text-xl sm:font-semibold">
                  {preamble}
                </div>
              )}
              {link?.slug && linkText && (
                <Button
                  className="mt-6 sm:mt-8"
                  as={Link}
                  href={getLink(link?.sys.id)?.path!}
                  color="white"
                  intent="outlined"
                >
                  {linkText}
                </Button>
              )}
            </div>
          )}
        </div>
        {backgroundWithWaveForm()}
      </div>
      <div className="relative mx-4 sm:-mt-40">
        {(content?.__typename === 'ComponentCorporate5050' && (
          <Corporate5050Wrapper className="mt-6 sm:mt-0">
            <Corporate5050 content={content as Corporate5050Props} />
          </Corporate5050Wrapper>
        )) ||
          (content?.__typename === 'ComponentCorporateNavigationBlock' && (
            <CorporateNavigationBlock
              content={content as CorporateNavigationBlockProps}
            />
          ))}
      </div>
    </>
  );
}

export default CorporateBanner;
